import React from 'react';
import {Button, Label} from 'reactstrap';
import {AvGroup} from 'availity-reactstrap-validation';
import {Fieldset} from 'primereact/fieldset';
import Translate from '../../shared/language/Translate';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import AvField from '../../shared/component/form/AvField';
import PurchaseRequisitionService from '../../shared/service/PurchaseRequisitionService';
import SupplierService from '../../shared/service/SupplierService';
import NumberFormat from 'react-number-format';
import {toast} from 'react-toastify';
import {PRODUCT_UNIT, RETURN_REASON_CODE, DELIVERY_WASTE_REASON_CODE, WEB_IMAGE_SERVER_URL} from '../../shared/utils/Constants';
import {hasSelectedSupplierOverride, normalizeItem} from '../purchaserequisition/PurchaseRequisitionUtils';
import {
    calculateOrderAmountFromBoxes,
    deriveOrderBoxCount,
    getActualOrderAmountTone
} from '../purchaserequisition/PurchaseRequisitionActualOrderUtils';
import DateInput from '../../shared/component/form/DateInput';
import Dialog from '../../shared/component/dialog/Dialog';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import '../../shared/styles/SettingsPage.css';

dayjs.extend(customParseFormat);

const requiredMark = <span style={{color: 'red'}}> (*)</span>;

export default class PurchaseRequisitionItemMobileSave extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            item: {},
            supplierList: [],
            costAlternativesExpanded: false,
            displaySupplierChangeDialog: false,
            packingVisualExpanded: false
        };
        this.purchaseRequisitionService = new PurchaseRequisitionService();
        this.supplierService = new SupplierService();
    }

    componentDidMount() {
        this.supplierService.findAll().then(response => {
            if (Array.isArray(response)) {
                this.setState({supplierList: response}, () => this.enrichMissingSupplierNames());
            }
        });
    }

    enrichMissingSupplierNames = () => {
        const item = this.state.item;
        if (!item) {
            return;
        }
        let next = item;
        let changed = false;
        if (!GeneralUtils.isNullOrEmpty(item.currentSupplierId) && GeneralUtils.isNullOrEmpty(item.currentSupplierName)) {
            const supplier = this.resolveSupplier(item.currentSupplierId);
            if (supplier?.name) {
                next = {...next, currentSupplierName: supplier.name};
                changed = true;
            }
        }
        if (!GeneralUtils.isNullOrEmpty(item.supplierId) && GeneralUtils.isNullOrEmpty(item.supplierName)) {
            const supplier = this.resolveSupplier(item.supplierId);
            if (supplier?.name) {
                next = {...next, supplierName: supplier.name};
                changed = true;
            }
        }
        if (changed) {
            this.setState({item: next});
        }
    };

    setData = (data) => {
        const item = Object.assign({}, data);
        const isDelivery = this.props.deliveryMode === true;
        const isReturn = this.props.returnMode === true;
        if (isDelivery && (item.deliveredBoxCount == null || item.deliveredBoxCount === '')) {
            item.deliveredBoxCount = 1;
        }
        if (isDelivery) {
            const docSupplierId = this.props.documentSupplierId ?? item.supplierId ?? item.currentSupplierId ?? null;
            const docSupplierName = this.props.documentSupplierName ?? item.supplierName ?? item.currentSupplierName ?? null;
            const docSupplierBssId = this.props.documentSupplierBssId ?? item.supplierBssId ?? null;
            Object.assign(item, this.applySupplierFields(item, docSupplierId, docSupplierName, docSupplierBssId));
        }
        if (!isDelivery && !isReturn
            && (item.deliveredBoxCount == null || item.deliveredBoxCount === '')
            && item.actualOrderAmount != null && item.boxSize != null) {
            const derived = deriveOrderBoxCount(item.actualOrderAmount, item.boxSize);
            if (derived != null) {
                item.deliveredBoxCount = derived;
                const orderQty = calculateOrderAmountFromBoxes(derived, item.boxSize);
                if (orderQty != null) {
                    item.actualOrderAmount = orderQty;
                    item.recommendedOrderAmount = orderQty;
                }
            }
        }
        if (!item.longName && item.itemName) {
            item.longName = item.itemName;
        }
        if (item.boxSizeFromProduct == null && !isDelivery && !isReturn) {
            item.boxSizeFromProduct = item.boxSize != null && String(item.boxSize).trim() !== '';
        }
        this.setState({
            item,
            costAlternativesExpanded: false,
            displaySupplierChangeDialog: false,
            packingVisualExpanded: false
        }, () => {
            if (!this.isReadOnly()) {
                this.focusBarcodeInput();
            }
            if (isDelivery) {
                this.updateDeliveryCalcFields({});
            } else if (!isReturn) {
                this.updateOrderCalcFields({});
            }
        });
    };

    isReadOnly = () => this.props.readOnly === true;

    focusBarcodeInput = () => {
        if (this.isReadOnly()) {
            return;
        }
        setTimeout(() => {
            if (this.barcodeRef) {
                this.barcodeRef.focus();
            }
        }, 100);
    };

    resolveSupplier = (supplierId) => {
        if (GeneralUtils.isNullOrEmpty(supplierId)) {
            return null;
        }
        const normalizedId = Number(supplierId);
        const supplierList = Array.isArray(this.state.supplierList) ? this.state.supplierList : [];
        const supplier = supplierList.find(s =>
            Number(s.id) === normalizedId || s.id == supplierId
        );
        if (!supplier) {
            return null;
        }
        return {
            id: supplier.id,
            name: supplier.name,
            bssId: supplier.bssId || supplier.deposSupplierBssId || null
        };
    };

    applySupplierFields = (item, supplierId, supplierName, supplierBssId) => ({
        ...item,
        supplierId,
        supplierName,
        supplierBssId,
        currentSupplierId: supplierId,
        currentSupplierName: supplierName
    });

    /** Sets product's original supplier only (does not clear/set selected override). */
    applyCurrentSupplierFields = (item, supplierId, supplierName, supplierBssId = null) => ({
        ...item,
        currentSupplierId: supplierId,
        currentSupplierName: supplierName,
        // Default bss follows current until an override is chosen.
        supplierBssId: item.supplierId ? item.supplierBssId : supplierBssId
    });

    /** Sets selected/override supplier used for PO grouping & checkout. */
    applySelectedSupplierFields = (item, supplierId, supplierName, supplierBssId, unitCost = undefined) => {
        const next = {
            ...item,
            supplierId,
            supplierName,
            supplierBssId: supplierBssId ?? item.supplierBssId
        };
        // No catalogue supplier yet — treat the chosen supplier as current too (checkout requires an id).
        if (GeneralUtils.isNullOrEmpty(item.currentSupplierId)) {
            next.currentSupplierId = supplierId;
            next.currentSupplierName = supplierName;
        }
        if (unitCost !== undefined) {
            next.currentCost = unitCost;
        }
        return next;
    };

    clearSelectedSupplier = () => {
        if (this.isReadOnly()) {
            return;
        }
        this.setState(prev => ({
            item: {
                ...prev.item,
                supplierId: null,
                supplierName: null,
                supplierBssId: this.resolveSupplier(prev.item.currentSupplierId)?.bssId
                    ?? prev.item.supplierBssId,
                currentCost: prev.item.originalCurrentCost ?? prev.item.currentCost,
                cheaperCostAvailable: (prev.item.costAlternatives || []).length > 0
            },
            displaySupplierChangeDialog: false
        }));
    };

    applyLookupToItem = (lookup) => {
        const isDelivery = this.props.deliveryMode === true;
        const isReturn = this.props.returnMode === true;
        let supplierId;
        let supplierName;
        let supplierBssId;
        if (isDelivery) {
            // DN supplier comes from document header — do not overwrite from product barcode.
            supplierId = this.props.documentSupplierId ?? this.state.item.supplierId ?? this.state.item.currentSupplierId ?? null;
            supplierName = this.props.documentSupplierName ?? this.state.item.supplierName ?? this.state.item.currentSupplierName ?? null;
            supplierBssId = this.props.documentSupplierBssId ?? this.state.item.supplierBssId ?? null;
        } else {
            const supplier = this.resolveSupplier(lookup.currentSupplierId || lookup.supplierId);
            supplierId = lookup.supplierId ?? lookup.currentSupplierId ?? supplier?.id ?? null;
            supplierName = lookup.supplierName ?? lookup.currentSupplierName ?? supplier?.name ?? null;
            supplierBssId = lookup.supplierBssId ?? supplier?.bssId ?? null;
        }
        let item = {
            ...this.state.item,
            barcode: lookup.barcode,
            itemName: lookup.itemName,
            longName: lookup.longName || lookup.itemName,
            webImagePath: lookup.webImagePath || null,
            itemBssId: lookup.itemBssId,
            itemUnitMeasure: lookup.itemUnitMeasure,
            itemSize: lookup.itemSize,
            boxSize: lookup.boxSize,
            boxSizeFromProduct: !isDelivery
                && lookup.boxSize != null
                && String(lookup.boxSize).trim() !== '',
            currentCost: isDelivery ? null : lookup.currentCost,
            originalCurrentCost: (isDelivery || isReturn) ? null : lookup.currentCost,
            minStock: (isDelivery || isReturn) ? null : lookup.minStock,
            maxStock: (isDelivery || isReturn) ? null : lookup.maxStock,
            currentStock: (isDelivery || isReturn) ? null : lookup.currentStock,
            damagedQuantity: isDelivery ? null : this.state.item.damagedQuantity,
            reasonCode: isReturn ? (this.state.item.reasonCode || '') : this.state.item.reasonCode,
            // New product lookup resets selected override (PR only).
            supplierId: isDelivery ? supplierId : null,
            supplierName: isDelivery ? supplierName : null
        };
        if (!isDelivery && !isReturn) {
            const baseQty = lookup.recommendedOrderAmount ?? lookup.actualOrderAmount;
            const boxes = deriveOrderBoxCount(baseQty, lookup.boxSize);
            const orderQty = calculateOrderAmountFromBoxes(boxes, lookup.boxSize);
            item.deliveredBoxCount = boxes;
            item.actualOrderAmount = orderQty;
            // Recommended shown as whole-box recalculated qty (boxes × units per box).
            item.recommendedOrderAmount = orderQty != null ? orderQty : lookup.recommendedOrderAmount;
        } else if (isDelivery) {
            item.deliveredBoxCount = this.state.item.deliveredBoxCount != null && this.state.item.deliveredBoxCount !== ''
                ? this.state.item.deliveredBoxCount
                : 1;
            item.actualOrderAmount = null;
            item.recommendedOrderAmount = null;
            item.supplierBssId = supplierBssId;
        } else {
            item.deliveredBoxCount = this.state.item.deliveredBoxCount;
            item.actualOrderAmount = null;
            item.recommendedOrderAmount = null;
        }
        if (isDelivery) {
            item = this.applySupplierFields(item, supplierId, supplierName, supplierBssId);
        } else {
            item = this.applyCurrentSupplierFields(item, supplierId, supplierName, supplierBssId);
            if (isReturn) {
                item = this.applySupplierFields(item, supplierId, supplierName, supplierBssId);
            }
        }
        this.setState({item}, () => {
            if (isDelivery) {
                this.updateDeliveryCalcFields({});
            } else if (!isReturn) {
                this.updateOrderCalcFields({});
            }
        });
    };

    findByBarcode = () => {
        if (this.isReadOnly()) {
            return;
        }
        const barcode = this.state.item?.barcode;
        if (GeneralUtils.isNullOrEmpty(barcode)) {
            return;
        }
        this.purchaseRequisitionService.findProductByBarcode(barcode).then(response => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                this.applyLookupToItem(response);
            } else {
                toast.warn('Barcode not Found!');
            }
        });
    };

    __handleChange = (e) => {
        if (this.isReadOnly()) {
            return;
        }
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        const name = e.target.name;
        let parsedValue = value;
        if (name === 'barcode' && value != null) {
            parsedValue = value.toString().replace(/[^0-9]/g, '');
        }
        this.setState(prev => ({
            item: {...prev.item, [name]: parsedValue}
        }));
    };

    __handleSupplierChange = (e) => {
        if (this.isReadOnly()) {
            return;
        }
        const rawValue = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        const supplierId = GeneralUtils.isNullOrEmpty(rawValue) ? null : Number(rawValue);
        const supplier = this.resolveSupplier(supplierId);
        this.setState(prev => ({
            item: this.applySupplierFields(
                prev.item,
                supplierId,
                supplier?.name ?? null,
                supplier?.bssId ?? null
            )
        }));
    };

    _handleEnterKeyDown = (e) => {
        if (e.target.name === 'barcode') {
            const allowedKeys = ['Backspace', 'Tab', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter'];
            const isDigit = e.key.length === 1 && /[0-9]/.test(e.key);
            if (!isDigit && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                return;
            }
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
            e.preventDefault();
            this.findByBarcode();
            if (this.barcodeRef) {
                this.barcodeRef.focus();
            }
        }
    };

    _handleEnterInput = (e) => {
        if (e.nativeEvent.inputType === 'insertLineBreak') {
            e.preventDefault();
            this.findByBarcode();
            if (this.barcodeRef) {
                this.barcodeRef.focus();
            }
        }
    };

    validateItem = (item) => {
        if (GeneralUtils.isNullOrEmpty(item.barcode)) {
            toast.warn('Barcode is required');
            return false;
        }
        if (this.props.returnMode) {
            const returnQty = item.actualOrderAmount;
            if (returnQty == null || String(returnQty).trim() === '') {
                toast.warn('Return Quantity is required');
                return false;
            }
            const returnQtyNumber = Number(returnQty);
            if (Number.isNaN(returnQtyNumber) || returnQtyNumber <= 0) {
                toast.warn('Return Quantity must be greater than zero');
                return false;
            }
            if (GeneralUtils.isNullOrEmpty(item.reasonCode)) {
                toast.warn('Reason Code is required');
                return false;
            }
            const supplierId = item.supplierId ?? item.currentSupplierId;
            if (GeneralUtils.isNullOrEmpty(supplierId)) {
                toast.warn('Supplier is required');
                return false;
            }
            return true;
        }
        if (this.props.deliveryMode) {
            const itemQty = item.deliveredBoxCount;
            const boxQuantity = item.boxSize;
            if (itemQty == null || String(itemQty).trim() === '') {
                toast.warn('Number of Boxes is required');
                return false;
            }
            const itemQtyNumber = Number(itemQty);
            if (Number.isNaN(itemQtyNumber) || itemQtyNumber < 0) {
                toast.warn('Number of Boxes must be a valid number');
                return false;
            }
            if (boxQuantity == null || String(boxQuantity).trim() === '') {
                toast.warn('Units per Box is required');
                return false;
            }
            const boxQuantityNumber = Number(boxQuantity);
            if (Number.isNaN(boxQuantityNumber) || boxQuantityNumber < 0) {
                toast.warn('Units per Box must be a valid number');
                return false;
            }
            const deliveryQty = itemQtyNumber * boxQuantityNumber;
            item.actualOrderAmount = deliveryQty;
            const supplierId = item.supplierId ?? item.currentSupplierId;
            if (GeneralUtils.isNullOrEmpty(supplierId)) {
                toast.warn('Supplier is required');
                return false;
            }
            if (item.damagedQuantity != null && String(item.damagedQuantity).trim() !== '') {
                const damagedNumber = Number(item.damagedQuantity);
                if (Number.isNaN(damagedNumber) || damagedNumber < 0) {
                    toast.warn('Waste Quantity must be a valid number');
                    return false;
                }
                if (damagedNumber > deliveryQty) {
                    toast.warn('Waste Quantity cannot be greater than Total Quantity');
                    return false;
                }
                if (damagedNumber > 0) {
                    if (GeneralUtils.isNullOrEmpty(item.reasonCode)) {
                        toast.warn('Waste Reason is required when Waste Quantity is entered');
                        return false;
                    }
                    if (item.reasonCode === 'EXPIRED' && GeneralUtils.isNullOrEmpty(item.expiryDate)) {
                        toast.warn('Expiry Date is required for Expired waste');
                        return false;
                    }
                }
            }
            return true;
        }
        if (this.props.webPopup) {
            const supplierId = item.supplierId ?? item.currentSupplierId;
            if (GeneralUtils.isNullOrEmpty(item.currentSupplierId) && GeneralUtils.isNullOrEmpty(supplierId)) {
                toast.warn('Supplier is required');
                return false;
            }
            if (GeneralUtils.isNullOrEmpty(item.itemName)) {
                toast.warn('Product Name is required');
                return false;
            }
            if (!this.props.returnMode && !this.props.deliveryMode) {
                if (item.boxSize == null || String(item.boxSize).trim() === '') {
                    toast.warn('Units per Box is required');
                    return false;
                }
                if (item.deliveredBoxCount == null || String(item.deliveredBoxCount).trim() === '') {
                    toast.warn('Number of Boxes is required');
                    return false;
                }
                const calculated = calculateOrderAmountFromBoxes(item.deliveredBoxCount, item.boxSize);
                if (calculated != null) {
                    item.actualOrderAmount = calculated;
                }
            }
        }
        const actualOrderAmount = item.actualOrderAmount;
        if (actualOrderAmount == null || String(actualOrderAmount).trim() === '') {
            toast.warn('Actual Order Amount is required');
            return false;
        }
        return true;
    };

    handleSave = (closeAfter = true) => {
        const item = normalizeItem({...this.state.item});
        if (!this.validateItem(item)) {
            return;
        }
        if (closeAfter) {
            this.props.saveItem(item);
        } else {
            this.props.saveItemAndNext(item);
        }
    };

    applyAlternativeSupplier = (alternative) => {
        if (!alternative || this.isReadOnly()) {
            return;
        }
        this.selectOverrideSupplier({
            id: alternative.supplierId ?? null,
            name: alternative.supplierName ?? null,
            bssId: alternative.supplierBssId ?? null
        });
    };

    selectOverrideSupplier = (supplier) => {
        if (!supplier || this.isReadOnly()) {
            return;
        }
        const currentId = this.state.item?.currentSupplierId;
        if (currentId != null && Number(supplier.id) === Number(currentId)) {
            toast.warn('Please select a supplier different from the current supplier');
            return;
        }
        const nextCost = supplier.unitCost != null && supplier.unitCost !== ''
            ? supplier.unitCost
            : null;
        this.setState(prev => ({
            item: this.applySelectedSupplierFields(
                {
                    ...prev.item,
                    cheaperCostAvailable: false,
                    currentCost: nextCost
                },
                supplier.id ?? null,
                supplier.name ?? null,
                supplier.bssId ?? null,
                nextCost
            ),
            displaySupplierChangeDialog: false
        }));
    };

    openSupplierChangeDialog = () => {
        if (this.isReadOnly()) {
            return;
        }
        this.setState({displaySupplierChangeDialog: true});
    };

    closeSupplierChangeDialog = () => {
        this.setState({displaySupplierChangeDialog: false});
    };

    getSupplierChangeOptions = (item) => {
        const currentId = item?.currentSupplierId != null ? Number(item.currentSupplierId) : null;
        const alternatives = (Array.isArray(item?.costAlternatives) ? item.costAlternatives : []).filter(alt =>
            alt?.supplierId == null || Number(alt.supplierId) !== currentId
        );
        const alternativeIds = new Set(
            alternatives
                .map(alt => alt.supplierId != null ? Number(alt.supplierId) : null)
                .filter(id => id != null)
        );
        const supplierList = Array.isArray(this.state.supplierList) ? this.state.supplierList : [];
        const otherSuppliers = supplierList
            .filter(s => currentId == null || Number(s.id) !== currentId)
            .filter(s => !alternativeIds.has(Number(s.id)))
            .map(s => ({
                supplierId: s.id,
                supplierName: s.name,
                supplierBssId: s.bssId || s.deposSupplierBssId || null,
                unitCost: null,
                isAlternative: false
            }));
        const cheapOptions = alternatives.map(alt => ({
            supplierId: alt.supplierId,
            supplierName: alt.supplierName,
            supplierBssId: alt.supplierBssId,
            unitCost: alt.unitCost,
            isAlternative: true
        }));
        return [...cheapOptions, ...otherSuppliers];
    };

    formatMoney = (value) => {
        if (value == null || value === '') {
            return '-';
        }
        const number = Number(value);
        if (Number.isNaN(number)) {
            return value;
        }
        return `£${number.toFixed(2)}`;
    };

    renderCheaperSupplierHint(item) {
        if (!this.props.webPopup || this.isReadOnly() || !item?.cheaperCostAvailable) {
            return null;
        }
        const count = (item.costAlternatives || []).length;
        if (count <= 0) {
            return null;
        }
        return (
            <span className="pr-cheaper-supplier-hint pr-cheaper-supplier-hint--inline" role="status">
                Cheaper option{count === 1 ? '' : 's'}
            </span>
        );
    }

    isFieldDisabled = (fieldDisabled = false) => this.isReadOnly() || fieldDisabled;

    renderNumberField = (name, label, fieldDisabled = false, required = false) => {
        const disabled = this.isFieldDisabled(fieldDisabled);
        const readonlyClass = disabled
            ? ' purchase-requisition-item-form__input--readonly'
            : ' purchase-requisition-item-form__input--editable';
        const rawValue = this.state.item[name];
        if (disabled && (rawValue == null || rawValue === '')) {
            return (
                <AvGroup>
                    <Label for={name}>
                        {label}
                        {required && requiredMark}
                    </Label>
                    <input
                        type="text"
                        className={`form-control${readonlyClass}`}
                        value="—"
                        disabled={true}
                        readOnly={true}
                    />
                </AvGroup>
            );
        }
        return (
            <AvGroup>
                <Label for={name}>
                    {label}
                    {required && requiredMark}
                </Label>
                <NumberFormat
                    value={rawValue}
                    className={`number-format-input form-control${readonlyClass}`}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    onValueChange={(values) => {
                        if (disabled) {
                            return;
                        }
                        this.setState(prev => ({
                            item: {...prev.item, [name]: values.value}
                        }));
                    }}
                    disabled={disabled}
                />
            </AvGroup>
        );
    };

    renderFooterButtons() {
        if (this.isReadOnly()) {
            return (
                <Button
                    color="outline-primary"
                    className="settings-page__btn--action"
                    onClick={this.props.closeItemDialog}
                >
                    Close
                </Button>
            );
        }
        return (
            <>
                <Button
                    color="outline-primary"
                    className="settings-page__btn--action"
                    onClick={() => this.handleSave(true)}
                >
                    Save
                </Button>
                <Button
                    color="outline-primary"
                    className="settings-page__btn--action"
                    onClick={() => this.handleSave(false)}
                >
                    Save &amp; Next
                </Button>
                <Button
                    color="outline-primary"
                    className="settings-page__btn--action"
                    onClick={this.props.closeItemDialog}
                >
                    Close
                </Button>
            </>
        );
    }

    renderFooter() {
        if (this.props.webPopup) {
            return (
                <div className="purchase-requisition-item-form__footer">
                    <div className="settings-page__toolbar settings-page__toolbar--end settings-page__toolbar--equal-height">
                        {this.renderFooterButtons()}
                    </div>
                </div>
            );
        }

        return (
            <div className="mobile-fixed-footer">
                <div className="mobile-fixed-footer-inner">
                    <Button
                        color="outline-primary"
                        style={{marginRight: '5px', height: '30px', width: '110px'}}
                        onClick={() => this.handleSave(true)}
                    >
                        Save
                    </Button>
                    <Button
                        color="outline-primary"
                        style={{marginRight: '5px', height: '30px', width: '130px'}}
                        onClick={() => this.handleSave(false)}
                    >
                        Save &amp; Next
                    </Button>
                    <Button
                        color="outline-primary"
                        style={{height: '30px', width: '110px'}}
                        onClick={this.props.closeItemDialog}
                    >
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    renderBarcodeField(item) {
        const disabled = this.isReadOnly();
        return (
            <AvGroup>
                <Label for="barcode">
                    <Translate contentKey="purchaseOrderItem.barcode">Barcode</Translate>
                    {!disabled && requiredMark}
                </Label>
                <div className="settings-page__barcode-input-row">
                    <AvField
                        type="text"
                        name="barcode"
                        id="barcode"
                        innerRef={(el) => (this.barcodeRef = el)}
                        className={`form-control${disabled ? ' purchase-requisition-item-form__input--readonly' : ''}`}
                        value={item.barcode || ''}
                        onChange={this.__handleChange}
                        onKeyDown={this._handleEnterKeyDown}
                        onInput={this._handleEnterInput}
                        autoComplete="off"
                        enterKeyHint="done"
                        disabled={disabled}
                    />
                    {!disabled &&
                        <Button
                            type="button"
                            color="outline-primary"
                            className="settings-page__btn--search"
                            onClick={this.findByBarcode}
                            title="Search"
                        >
                            <i className="pi pi-search" aria-hidden="true"/>
                        </Button>
                    }
                </div>
            </AvGroup>
        );
    }

    renderProductNameField(item, isWeb) {
        const forceReadonly = isWeb && !this.props.deliveryMode && !this.props.returnMode;
        const disabled = this.isReadOnly() || forceReadonly;
        return (
            <AvGroup>
                <Label for="itemName">
                    <Translate contentKey="purchaseOrderItem.itemName">Product Name</Translate>
                    {isWeb && !disabled && requiredMark}
                </Label>
                <AvField
                    type="text"
                    name="itemName"
                    className={disabled ? 'purchase-requisition-item-form__input--readonly' : 'purchase-requisition-item-form__input--editable'}
                    value={item.itemName || ''}
                    onChange={this.__handleChange}
                    disabled={disabled}
                />
            </AvGroup>
        );
    }

    renderSupplierField(item, isWeb) {
        // Delivery note supplier is fixed from document header.
        if (this.props.deliveryMode) {
            return (
                <AvGroup>
                    <Label for="currentSupplierId">Supplier</Label>
                    <AvField
                        type="text"
                        name="supplierName"
                        className="purchase-requisition-item-form__input--readonly"
                        value={item.supplierName || item.currentSupplierName || this.props.documentSupplierName || ''}
                        disabled={true}
                    />
                </AvGroup>
            );
        }
        // Return keeps editable supplier select.
        if (this.props.returnMode || !isWeb) {
            const disabled = this.isReadOnly();
            return (
                <AvGroup>
                    <Label for="currentSupplierId">
                        Supplier
                        {isWeb && !disabled && requiredMark}
                    </Label>
                    <AvField
                        type="select"
                        name="supplierId"
                        className={disabled ? 'purchase-requisition-item-form__input--readonly' : ''}
                        value={item.supplierId || item.currentSupplierId || ''}
                        onChange={this.__handleSupplierChange}
                        disabled={disabled}
                    >
                        <option value="" key=""/>
                        {(Array.isArray(this.state.supplierList) ? this.state.supplierList : []).map(element => (
                            <option value={element.id} key={element.id}>{element.name}</option>
                        ))}
                    </AvField>
                </AvGroup>
            );
        }
        return this.renderWebSupplierFields(item);
    }

    renderWebSupplierFields(item) {
        const disabled = this.isReadOnly();
        const override = hasSelectedSupplierOverride(item);
        const cheaperHint = this.renderCheaperSupplierHint(item);
        return (
            <>
                <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--supplier">
                    <div className={`purchase-requisition-item-form__cell purchase-requisition-item-form__cell--${override ? '4' : '6'}`}>
                        <AvGroup className="pr-supplier-field">
                            <Label for="currentSupplierName">
                                Current Supplier
                                {!disabled && requiredMark}
                            </Label>
                            <div className="pr-supplier-current-row">
                                <input
                                    type="text"
                                    className="form-control purchase-requisition-item-form__input--readonly"
                                    value={item.currentSupplierName || ''}
                                    disabled={true}
                                    readOnly={true}
                                />
                                {!disabled &&
                                    <Button
                                        color="outline-primary"
                                        className="settings-page__btn--action pr-supplier-change-btn"
                                        onClick={this.openSupplierChangeDialog}
                                    >
                                        Change
                                    </Button>
                                }
                            </div>
                            {cheaperHint}
                        </AvGroup>
                    </div>
                    {override &&
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                            <AvGroup className="pr-supplier-field">
                                <Label for="selectedSupplierName">
                                    Selected Supplier
                                    <span className="pr-supplier-changed-badge">Changed</span>
                                </Label>
                                <div className="pr-supplier-selected-row">
                                    <input
                                        type="text"
                                        className="form-control purchase-requisition-item-form__input--readonly pr-supplier-selected--changed"
                                        value={item.supplierName || ''}
                                        disabled={true}
                                        readOnly={true}
                                    />
                                    {!disabled &&
                                        <Button
                                            color="outline-secondary"
                                            className="settings-page__btn--action pr-supplier-clear-btn"
                                            onClick={this.clearSelectedSupplier}
                                        >
                                            Clear
                                        </Button>
                                    }
                                </div>
                            </AvGroup>
                        </div>
                    }
                    <div className={`purchase-requisition-item-form__cell purchase-requisition-item-form__cell--${override ? '4' : '6'}`}>
                        <div className="pr-supplier-field">
                            {this.renderCurrentCostField(item)}
                        </div>
                    </div>
                </div>
                {this.renderSupplierChangeDialog(item)}
            </>
        );
    }

    renderSupplierChangeDialog(item) {
        const options = this.getSupplierChangeOptions(item);
        return (
            <Dialog
                visible={this.state.displaySupplierChangeDialog === true}
                header="Change Supplier"
                onHide={this.closeSupplierChangeDialog}
                style={{width: '560px', maxWidth: '95vw'}}
            >
                <div className="pr-supplier-change-dialog">
                    <p className="pr-supplier-change-dialog__hint">
                        Select a supplier different from the current supplier
                        {item.currentSupplierName ? ` (${item.currentSupplierName})` : ''}.
                    </p>
                    {options.length === 0
                        ? <div className="text-muted">No other suppliers available.</div>
                        : (
                            <div className="pr-supplier-change-dialog__table-wrap">
                                <table className="pr-supplier-change-dialog__table">
                                    <thead>
                                        <tr>
                                            <th>Supplier</th>
                                            <th className="pr-supplier-change-dialog__cost-col">Unit Cost</th>
                                            <th className="pr-supplier-change-dialog__action-col"/>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {options.map((option, index) => (
                                            <tr key={`${option.supplierId || option.supplierName || index}`}>
                                                <td>{option.supplierName || ''}</td>
                                                <td className="pr-supplier-change-dialog__cost-col">
                                                    {option.unitCost != null ? this.formatMoney(option.unitCost) : ''}
                                                </td>
                                                <td className="pr-supplier-change-dialog__action-col">
                                                    <Button
                                                        color="outline-primary"
                                                        size="sm"
                                                        className="settings-page__btn--action"
                                                        onClick={() => this.selectOverrideSupplier({
                                                            id: option.supplierId,
                                                            name: option.supplierName,
                                                            bssId: option.supplierBssId,
                                                            unitCost: option.unitCost
                                                        })}
                                                    >
                                                        Select
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    }
                    <div className="settings-page__actions settings-page__actions--dialog-footer" style={{justifyContent: 'flex-start'}}>
                        <Button
                            color="outline-primary"
                            className="settings-page__btn--cancel"
                            onClick={this.closeSupplierChangeDialog}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    }

    renderCurrentCostField(item) {
        const disabled = this.isReadOnly();
        const overridden = hasSelectedSupplierOverride(item);
        const empty = item.currentCost == null || item.currentCost === '';
        const readonlyClass = disabled
            ? ' purchase-requisition-item-form__input--readonly'
            : ' purchase-requisition-item-form__input--editable';
        const clearedClass = overridden && empty ? ' pr-current-cost--cleared' : '';
        return (
            <AvGroup>
                <Label for="currentCost">Current Cost</Label>
                <NumberFormat
                    value={empty ? '' : item.currentCost}
                    className={`number-format-input form-control${readonlyClass}${clearedClass}`}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    placeholder={overridden && empty ? '—' : undefined}
                    onValueChange={(values) => {
                        if (disabled) {
                            return;
                        }
                        this.setState(prev => ({
                            item: {...prev.item, currentCost: values.value === '' ? null : values.value}
                        }));
                    }}
                    disabled={disabled}
                />
            </AvGroup>
        );
    }

    renderReturnUnitCostField(item) {
        const empty = item.currentCost == null || item.currentCost === '';
        return (
            <AvGroup>
                <Label for="returnUnitCost">Unit Cost</Label>
                <input
                    id="returnUnitCost"
                    type="text"
                    className="form-control purchase-requisition-item-form__input--readonly"
                    value={empty ? '—' : this.formatMoney(item.currentCost)}
                    disabled={true}
                    readOnly={true}
                />
            </AvGroup>
        );
    }

    getActualQtyLabel = () => this.props.deliveryMode ? 'Total Quantity' : 'Actual Order Amount';

    getBoxQuantityLabel = () => 'Units per Box';

    getItemQuantityLabel = () => 'Number of Boxes';

    getDeliveryQuantityLabel = () => 'Total Quantity';

    getDamagedQuantityLabel = () => 'Waste Quantity';

    getNetQuantityLabel = () => 'Net Quantity';

    renderDeliveryBoxVisual = (item) => {
        const boxes = Number(item?.deliveredBoxCount);
        const unitsPerBox = Number(item?.boxSize);
        if (Number.isNaN(boxes) || Number.isNaN(unitsPerBox)
            || item?.deliveredBoxCount === '' || item?.deliveredBoxCount == null
            || item?.boxSize === '' || item?.boxSize == null
            || boxes <= 0 || unitsPerBox <= 0) {
            return null;
        }

        const boxCount = Math.floor(boxes);
        const perBox = Math.floor(unitsPerBox);
        const total = boxCount * perBox;
        const damagedRaw = Number(item?.damagedQuantity);
        const damagedCount = Number.isNaN(damagedRaw)
            || item?.damagedQuantity === ''
            || item?.damagedQuantity == null
            ? 0
            : Math.max(0, Math.min(Math.floor(damagedRaw), total));
        const netCount = total - damagedCount;
        const expanded = this.state.packingVisualExpanded === true;
        const showGrid = expanded;

        const maxBoxes = 12;
        const maxUnits = 24;
        const shownBoxes = Math.min(boxCount, maxBoxes);
        const shownUnits = Math.min(perBox, maxUnits);
        const cols = Math.min(6, Math.max(2, Math.ceil(Math.sqrt(shownUnits))));
        const damagedStartIndex = total - damagedCount;

        return (
            <div className={`dn-box-visual${showGrid ? '' : ' dn-box-visual--collapsed'}`}>
                <div className="dn-box-visual__header">
                    <div className="dn-box-visual__summary">
                        <strong>{boxCount}</strong> box{boxCount === 1 ? '' : 'es'}
                        <span className="dn-box-visual__times">×</span>
                        <strong>{perBox}</strong> unit{perBox === 1 ? '' : 's'}
                        <span className="dn-box-visual__eq">=</span>
                        <strong>{total}</strong> total
                        {damagedCount > 0 &&
                            <>
                                <span className="dn-box-visual__sep">·</span>
                                <strong className="dn-box-visual__damaged-text">{damagedCount}</strong> waste
                                <span className="dn-box-visual__sep">·</span>
                                <strong>{netCount}</strong> net
                            </>
                        }
                    </div>
                    <button
                        type="button"
                        className="dn-box-visual__toggle"
                        onClick={() => this.setState(prev => ({packingVisualExpanded: !prev.packingVisualExpanded}))}
                    >
                        {showGrid ? 'Hide packing' : 'Show packing'}
                    </button>
                </div>
                {showGrid &&
                    <>
                        {damagedCount > 0 &&
                            <div className="dn-box-visual__legend">
                                <span className="dn-box-visual__legend-item">
                                    <span className="dn-box-visual__unit"/> OK
                                </span>
                                <span className="dn-box-visual__legend-item">
                                    <span className="dn-box-visual__unit dn-box-visual__unit--damaged"/> Waste
                                </span>
                            </div>
                        }
                        <div className="dn-box-visual__boxes" aria-hidden="true">
                            {Array.from({length: shownBoxes}).map((_, boxIndex) => (
                                <div className="dn-box-visual__box" key={`box-${boxIndex}`}>
                                    <div
                                        className="dn-box-visual__units"
                                        style={{gridTemplateColumns: `repeat(${cols}, 1fr)`}}
                                    >
                                        {Array.from({length: shownUnits}).map((__, unitIndex) => {
                                            const globalIndex = boxIndex * perBox + unitIndex;
                                            const isDamaged = damagedCount > 0 && globalIndex >= damagedStartIndex;
                                            return (
                                                <span
                                                    className={`dn-box-visual__unit${isDamaged ? ' dn-box-visual__unit--damaged' : ''}`}
                                                    key={`u-${boxIndex}-${unitIndex}`}
                                                />
                                            );
                                        })}
                                    </div>
                                    <div className="dn-box-visual__box-label">{perBox}</div>
                                </div>
                            ))}
                            {boxCount > maxBoxes &&
                                <div className="dn-box-visual__more">+{boxCount - maxBoxes} more boxes</div>
                            }
                        </div>
                        {perBox > maxUnits &&
                            <div className="dn-box-visual__note">
                                Showing {maxUnits} of {perBox} units per box
                            </div>
                        }
                    </>
                }
            </div>
        );
    };

    getDeliveryQuantity = (item) => {
        const itemQty = Number(item?.deliveredBoxCount);
        const boxQuantity = Number(item?.boxSize);
        if (Number.isNaN(itemQty) || Number.isNaN(boxQuantity)
            || item?.deliveredBoxCount === '' || item?.deliveredBoxCount == null
            || item?.boxSize === '' || item?.boxSize == null) {
            return null;
        }
        return itemQty * boxQuantity;
    };

    getNetQuantity = (item) => {
        const deliveryQty = this.getDeliveryQuantity(item);
        if (deliveryQty == null) {
            return null;
        }
        const damaged = Number(item?.damagedQuantity);
        const damagedValue = Number.isNaN(damaged) || item?.damagedQuantity === '' || item?.damagedQuantity == null
            ? 0
            : damaged;
        return deliveryQty - damagedValue;
    };

    calculateDeliveryQty = (boxes, boxQty) => {
        const boxCount = Number(boxes);
        const perBox = Number(boxQty);
        if (Number.isNaN(boxCount) || Number.isNaN(perBox) || boxes === '' || boxes == null || boxQty === '' || boxQty == null) {
            return null;
        }
        return boxCount * perBox;
    };

    updateDeliveryCalcFields = (patch) => {
        this.setState(prev => {
            const item = {...prev.item, ...patch};
            const calculated = this.calculateDeliveryQty(item.deliveredBoxCount, item.boxSize);
            if (calculated != null) {
                item.actualOrderAmount = calculated;
            }
            if (Object.prototype.hasOwnProperty.call(patch, 'damagedQuantity')) {
                const damagedNumber = Number(item.damagedQuantity);
                const damagedEmpty = item.damagedQuantity == null || String(item.damagedQuantity).trim() === ''
                    || Number.isNaN(damagedNumber) || damagedNumber <= 0;
                if (damagedEmpty) {
                    item.reasonCode = null;
                    item.expiryDate = null;
                    item.description = null;
                }
            }
            if (Object.prototype.hasOwnProperty.call(patch, 'damagedQuantity')
                && item.damagedQuantity != null
                && String(item.damagedQuantity).trim() !== '') {
                const damagedNumber = Number(item.damagedQuantity);
                const totalQty = calculated != null ? calculated : this.getDeliveryQuantity(item);
                if (!Number.isNaN(damagedNumber) && totalQty != null && damagedNumber > totalQty) {
                    toast.warn('Waste Quantity cannot be greater than Total Quantity');
                    item.damagedQuantity = prev.item.damagedQuantity;
                    item.reasonCode = prev.item.reasonCode;
                    item.expiryDate = prev.item.expiryDate;
                    item.description = prev.item.description;
                }
            } else if (calculated != null
                && item.damagedQuantity != null
                && String(item.damagedQuantity).trim() !== '') {
                const damagedNumber = Number(item.damagedQuantity);
                if (!Number.isNaN(damagedNumber) && damagedNumber > calculated) {
                    toast.warn('Waste Quantity cannot be greater than Total Quantity');
                    item.damagedQuantity = null;
                    item.reasonCode = null;
                    item.expiryDate = null;
                    item.description = null;
                }
            }
            const nextState = {item};
            if (Object.prototype.hasOwnProperty.call(patch, 'damagedQuantity')) {
                const damagedNumber = Number(item.damagedQuantity);
                if (!Number.isNaN(damagedNumber) && damagedNumber > 0) {
                    nextState.packingVisualExpanded = true;
                }
            }
            return nextState;
        });
    };

    __handleWasteReasonChange = (e) => {
        if (this.isReadOnly()) {
            return;
        }
        const reasonCode = e.target.value || null;
        this.setState(prev => ({
            item: {
                ...prev.item,
                reasonCode,
                expiryDate: reasonCode === 'EXPIRED' ? prev.item.expiryDate : null
            }
        }));
    };

    hasWasteQuantity = (item) => {
        const damaged = Number(item?.damagedQuantity);
        return item?.damagedQuantity != null
            && String(item.damagedQuantity).trim() !== ''
            && !Number.isNaN(damaged)
            && damaged > 0;
    };

    updateOrderCalcFields = (patch) => {
        this.setState(prev => {
            const item = {...prev.item, ...patch};
            if (item.deliveredBoxCount != null && item.deliveredBoxCount !== '') {
                const boxes = Number(item.deliveredBoxCount);
                if (!Number.isNaN(boxes) && !Number.isInteger(boxes)) {
                    item.deliveredBoxCount = Math.ceil(boxes);
                }
            }
            const calculated = calculateOrderAmountFromBoxes(item.deliveredBoxCount, item.boxSize);
            if (calculated != null) {
                item.actualOrderAmount = calculated;
            }
            return {item};
        });
    };

    renderReadOnlyValue = (label, value, options = {}) => {
        let displayValue;
        if (options.isCurrency) {
            const num = Number(value);
            displayValue = !Number.isNaN(num) ? `£${num.toFixed(2)}` : '£0.00';
        } else if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
            displayValue = '—';
        } else {
            displayValue = value;
        }
        return (
            <div className="pr-item-readonly">
                <div className="pr-item-readonly__label">{label}</div>
                <div className="pr-item-readonly__value">{displayValue}</div>
            </div>
        );
    };

    getUnitDisplayName = (unitValue) => {
        if (unitValue == null || unitValue === '') {
            return null;
        }
        const match = (PRODUCT_UNIT || []).find(element =>
            String(element.value) === String(unitValue) || String(element.name) === String(unitValue)
        );
        return match ? match.name : unitValue;
    };

    renderProductImage = (item) => {
        const hasImage = !GeneralUtils.isNullOrEmpty(item?.webImagePath);
        return (
            <div className={`pr-item-product-image${hasImage ? '' : ' pr-item-product-image--empty'}`}>
                {hasImage
                    ? <img src={WEB_IMAGE_SERVER_URL + item.webImagePath} alt={item.longName || item.itemName || 'Product'}/>
                    : <span>No image</span>
                }
            </div>
        );
    };

    renderActualOrderAmountField = (item, required = true) => {
        const tone = getActualOrderAmountTone(item);
        const toneClass = tone ? ` pr-actual-order-amount--${tone}` : '';
        const empty = item.actualOrderAmount == null || item.actualOrderAmount === '';
        return (
            <AvGroup>
                <Label for="actualOrderAmount" className="pr-item-readonly__label">
                    Actual Order Amount
                    {required && requiredMark}
                </Label>
                <NumberFormat
                    value={empty ? '' : item.actualOrderAmount}
                    className={`number-format-input form-control purchase-requisition-item-form__input--readonly pr-actual-order-amount${toneClass}`}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    placeholder="—"
                    disabled={true}
                />
            </AvGroup>
        );
    };

    __handleExpiryDateChange = (e) => {
        if (this.isReadOnly()) {
            return;
        }
        const raw = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        let expiryDate = null;
        if (raw !== '' && raw != null) {
            const parsed = dayjs(Number(raw));
            expiryDate = parsed.isValid() ? parsed.format('DD/MM/YYYY') : null;
        }
        this.setState(prev => ({
            item: {...prev.item, expiryDate}
        }));
    };

    getExpiryDateInputValue = (item) => {
        if (GeneralUtils.isNullOrEmpty(item?.expiryDate)) {
            return '';
        }
        const parsed = dayjs(item.expiryDate, 'DD/MM/YYYY');
        return parsed.isValid() ? parsed.valueOf() : '';
    };

    renderDeliveryNumberField = (name, label, required = false, onValueChange, fieldDisabled = false) => {
        const disabled = this.isFieldDisabled(fieldDisabled);
        const readonlyClass = disabled
            ? ' purchase-requisition-item-form__input--readonly'
            : ' purchase-requisition-item-form__input--editable';
        const rawValue = this.state.item[name];
        if (disabled && (rawValue == null || rawValue === '')) {
            return (
                <AvGroup>
                    <Label for={name}>
                        {label}
                        {required && !this.isReadOnly() && requiredMark}
                    </Label>
                    <input
                        type="text"
                        className={`form-control${readonlyClass}`}
                        value="—"
                        disabled={true}
                        readOnly={true}
                    />
                </AvGroup>
            );
        }
        return (
            <AvGroup>
                <Label for={name}>
                    {label}
                    {required && !disabled && requiredMark}
                </Label>
                <NumberFormat
                    value={rawValue}
                    className={`number-format-input form-control${readonlyClass}`}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    onValueChange={(values) => {
                        if (disabled) {
                            return;
                        }
                        if (onValueChange) {
                            onValueChange(values.value);
                            return;
                        }
                        this.setState(prev => ({
                            item: {...prev.item, [name]: values.value}
                        }));
                    }}
                    disabled={disabled}
                />
            </AvGroup>
        );
    };

    renderDeliveryFormBody(item) {
        const deliveryQty = this.getDeliveryQuantity(item);
        const netQty = this.getNetQuantity(item);
        return (
            <div className="purchase-requisition-item-form__grid">
                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Product</div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                            {this.renderBarcodeField(item)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--8">
                            <AvGroup>
                                <Label for="itemName">Product Name</Label>
                                <AvField
                                    type="text"
                                    name="itemName"
                                    className="purchase-requisition-item-form__input--readonly"
                                    value={item.itemName || ''}
                                    disabled={true}
                                />
                            </AvGroup>
                        </div>
                    </div>
                </div>

                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Quantity</div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                            {this.renderDeliveryNumberField(
                                'boxSize',
                                this.getBoxQuantityLabel(),
                                true,
                                (value) => this.updateDeliveryCalcFields({boxSize: value})
                            )}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                            {this.renderDeliveryNumberField(
                                'deliveredBoxCount',
                                this.getItemQuantityLabel(),
                                true,
                                (value) => this.updateDeliveryCalcFields({deliveredBoxCount: value})
                            )}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                            <AvGroup>
                                <Label for="deliveryQuantity">{this.getDeliveryQuantityLabel()}</Label>
                                <input
                                    type="text"
                                    className="form-control purchase-requisition-item-form__input--readonly"
                                    value={deliveryQty == null ? '—' : deliveryQty}
                                    disabled={true}
                                    readOnly={true}
                                />
                            </AvGroup>
                        </div>
                    </div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                            {this.renderDeliveryNumberField(
                                'damagedQuantity',
                                this.getDamagedQuantityLabel(),
                                false,
                                (value) => this.updateDeliveryCalcFields({damagedQuantity: value})
                            )}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                            <AvGroup>
                                <Label for="netQuantity">{this.getNetQuantityLabel()}</Label>
                                <input
                                    type="text"
                                    className="form-control purchase-requisition-item-form__input--readonly"
                                    value={netQty == null ? '—' : netQty}
                                    disabled={true}
                                    readOnly={true}
                                />
                            </AvGroup>
                        </div>
                        {this.hasWasteQuantity(item) &&
                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                <AvGroup>
                                    <Label for="reasonCode">
                                        Waste Reason
                                        {!this.isReadOnly() && requiredMark}
                                    </Label>
                                    <AvField
                                        type="select"
                                        name="reasonCode"
                                        className={this.isReadOnly()
                                            ? 'purchase-requisition-item-form__input--readonly'
                                            : 'purchase-requisition-item-form__input--editable'}
                                        value={item.reasonCode || ''}
                                        onChange={this.__handleWasteReasonChange}
                                        disabled={this.isReadOnly()}
                                    >
                                        <option value="" key=""/>
                                        {DELIVERY_WASTE_REASON_CODE.map(element => (
                                            <option value={element.value} key={element.value}>{element.name}</option>
                                        ))}
                                    </AvField>
                                </AvGroup>
                            </div>
                        }
                    </div>
                    {this.hasWasteQuantity(item) && item.reasonCode === 'EXPIRED' &&
                        <div className="purchase-requisition-item-form__row">
                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                                <AvGroup>
                                    <Label for="expiryDate">
                                        Expiry Date
                                        {!this.isReadOnly() && requiredMark}
                                    </Label>
                                    <DateInput
                                        name="expiryDate"
                                        format="DD/MM/YYYY"
                                        value={this.getExpiryDateInputValue(item)}
                                        onChange={this.__handleExpiryDateChange}
                                        disabled={this.isReadOnly()}
                                    />
                                </AvGroup>
                            </div>
                        </div>
                    }
                    {this.hasWasteQuantity(item) &&
                        <div className="purchase-requisition-item-form__row">
                            <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--12">
                                <AvGroup>
                                    <Label for="description">
                                        Waste Description
                                    </Label>
                                    <AvField
                                        type="text"
                                        name="description"
                                        maxLength={50}
                                        className={this.isReadOnly()
                                            ? 'purchase-requisition-item-form__input--readonly'
                                            : 'purchase-requisition-item-form__input--editable'}
                                        value={item.description || ''}
                                        onChange={this.__handleChange}
                                        disabled={this.isReadOnly()}
                                    />
                                </AvGroup>
                            </div>
                        </div>
                    }
                    {this.props.webPopup === true && this.renderDeliveryBoxVisual(item)}
                </div>
            </div>
        );
    }

    renderWebFormBody(item) {
        const isWeb = true;
        const isDelivery = this.props.deliveryMode === true;
        if (isDelivery) {
            return this.renderDeliveryFormBody(item);
        }
        return (
            <div className="purchase-requisition-item-form__grid">
                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Identification</div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                            {this.renderBarcodeField(item)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--8">
                            {this.renderProductNameField(item, isWeb)}
                        </div>
                    </div>
                </div>

                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Packaging &amp; Amount</div>
                    <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            {this.renderReadOnlyValue('Item Qty', item.itemSize)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            {this.renderReadOnlyValue('Unit', this.getUnitDisplayName(item.itemUnitMeasure))}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            {this.renderDeliveryNumberField(
                                'boxSize',
                                this.getBoxQuantityLabel(),
                                true,
                                (value) => this.updateOrderCalcFields({boxSize: value}),
                                false
                            )}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            {this.renderDeliveryNumberField(
                                'deliveredBoxCount',
                                this.getItemQuantityLabel(),
                                true,
                                (value) => this.updateOrderCalcFields({deliveredBoxCount: value}),
                                false
                            )}
                        </div>
                    </div>
                    <div className="purchase-requisition-item-form__row purchase-requisition-item-form__row--packaging">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            {this.renderReadOnlyValue('Recommended Order Amount', item.recommendedOrderAmount)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            {this.renderActualOrderAmountField(item, true)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--2">
                            {this.renderReadOnlyValue('Current Stock', item.currentStock)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--2">
                            {this.renderReadOnlyValue('Min Stock', item.minStock)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--2">
                            {this.renderReadOnlyValue('Max Stock', item.maxStock)}
                        </div>
                    </div>
                    {this.renderDeliveryBoxVisual(item)}
                </div>

                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Supplier</div>
                    {this.renderWebSupplierFields(item)}
                </div>
            </div>
        );
    }

    renderReturnFormBody(item) {
        const disabled = this.isReadOnly();
        return (
            <div className="purchase-requisition-item-form__grid">
                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Product</div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                            {this.renderBarcodeField(item)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--8">
                            <AvGroup>
                                <Label for="itemName">Product Name</Label>
                                <AvField
                                    type="text"
                                    name="itemName"
                                    className="purchase-requisition-item-form__input--readonly"
                                    value={item.itemName || ''}
                                    disabled={true}
                                />
                            </AvGroup>
                        </div>
                    </div>
                </div>

                <div className="purchase-requisition-item-form__section">
                    <div className="purchase-requisition-item-form__section-title">Return</div>
                    <div className="purchase-requisition-item-form__row">
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            {this.renderNumberField('actualOrderAmount', 'Return Quantity', false, true)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            <AvGroup>
                                <Label for="reasonCode">
                                    Reason Code
                                    {!disabled && requiredMark}
                                </Label>
                                <AvField
                                    type="select"
                                    name="reasonCode"
                                    className={disabled ? 'purchase-requisition-item-form__input--readonly' : 'purchase-requisition-item-form__input--editable'}
                                    value={item.reasonCode || ''}
                                    onChange={this.__handleChange}
                                    disabled={disabled}
                                >
                                    <option value="" key=""/>
                                    {RETURN_REASON_CODE.map(element => (
                                        <option value={element.value} key={element.value}>{element.name}</option>
                                    ))}
                                </AvField>
                            </AvGroup>
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            {this.renderSupplierField(item, true)}
                        </div>
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--3">
                            {this.renderReturnUnitCostField(item)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderFormBody(item) {
        const isWeb = this.props.webPopup === true;
        if (this.props.deliveryMode) {
            return this.renderDeliveryFormBody(item);
        }
        if (this.props.returnMode) {
            return this.renderReturnFormBody(item);
        }
        if (isWeb) {
            return this.renderWebFormBody(item);
        }
        return (
            <div className="purchase-requisition-item-form__grid">
                <div className="purchase-requisition-item-form__row">
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                        {this.renderBarcodeField(item)}
                    </div>
                </div>

                <div className="purchase-requisition-item-form__row">
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                        {this.renderProductNameField(item, isWeb)}
                    </div>
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--2">
                        {this.renderNumberField('itemSize', 'Item Qty')}
                    </div>
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--2">
                        <AvGroup>
                            <Label for="itemUnitMeasure">Unit</Label>
                            <AvField
                                type="select"
                                name="itemUnitMeasure"
                                className={this.isReadOnly() ? 'purchase-requisition-item-form__input--readonly' : ''}
                                value={item.itemUnitMeasure || ''}
                                onChange={this.__handleChange}
                                disabled={this.isReadOnly()}
                            >
                                <option value="" key=""/>
                                {PRODUCT_UNIT.map(element => (
                                    <option value={element.value} key={element.value}>{element.name}</option>
                                ))}
                            </AvField>
                        </AvGroup>
                    </div>
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--2">
                        {this.renderNumberField('boxSize', 'Box Qty')}
                    </div>
                </div>

                <div className="purchase-requisition-item-form__row">
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                        {this.renderSupplierField(item, isWeb)}
                    </div>
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                        {this.renderCurrentCostField(item)}
                    </div>
                </div>

                <div className="purchase-requisition-item-form__row">
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                        {this.renderNumberField('currentStock', 'Current Stock', true)}
                    </div>
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                        {this.renderNumberField('minStock', 'Min Stock', true)}
                    </div>
                    <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--4">
                        {this.renderNumberField('maxStock', 'Max Stock', true)}
                    </div>
                </div>

                <div className="purchase-requisition-item-form__row">
                    {!this.props.deliveryMode &&
                        <div className="purchase-requisition-item-form__cell purchase-requisition-item-form__cell--6">
                            {this.renderNumberField('recommendedOrderAmount', 'Recommended Order Amount', true, false)}
                        </div>
                    }
                    <div className={`purchase-requisition-item-form__cell purchase-requisition-item-form__cell--${this.props.deliveryMode ? '12' : '6'}`}>
                        {this.renderNumberField('actualOrderAmount', this.getActualQtyLabel(), false, !this.isReadOnly())}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const item = this.state.item || {};
        const formBody = this.renderFormBody(item);

        if (this.props.webPopup) {
            return (
                <div className="purchase-requisition-item-form purchase-requisition-item-form--web">
                    {formBody}
                    {this.renderFooter()}
                </div>
            );
        }

        return (
            <div>
                <Fieldset legend="Product">
                    {formBody}
                </Fieldset>
                {this.renderFooter()}
            </div>
        );
    }
}
